/* ==========================================================================
   Two-factor settings card (PORTAL-187)
   Lets the signed-in user turn authenticator-app 2FA on (scan a QR code, then
   confirm a code) or off (confirm a code). Shared by the provider profile, the
   pharmacy-admin account page and the internal security page; each passes the
   API calls for its own role.
   ========================================================================== */

import { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { ShieldCheck } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Alert } from '@/components/ui/Alert'
import { Spinner } from '@/components/ui/Spinner'
import { useToast } from '@/context/ToastContext'
import { normalizeTwoFactorCode } from '@/utils/twoFactorDevice'
import type { TwoFactorSettingsApi, TwoFactorSetupResponse } from '@/api/types'

export interface TwoFactorSettingsCardProps {
  api: TwoFactorSettingsApi
  className?: string
}

type Dialog = 'enable' | 'disable' | null

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Please try again.'
}

/** Group the base32 key in fours so it is easier to type by hand. */
function formatSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim()
}

export function TwoFactorSettingsCard({ api, className = '' }: TwoFactorSettingsCardProps) {
  const toast = useToast()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [dialog, setDialog] = useState<Dialog>(null)
  const [setup, setSetup] = useState<TwoFactorSetupResponse | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .getStatus()
      .then((status) => {
        if (!cancelled) setEnabled(status.enabled)
      })
      .catch((err) => {
        if (!cancelled) setLoadError(errorMessage(err))
      })
    return () => {
      cancelled = true
    }
  }, [api])

  const close = () => {
    setDialog(null)
    setSetup(null)
    setCode('')
    setError(null)
  }

  const openEnable = async () => {
    setDialog('enable')
    setBusy(true)
    setError(null)
    try {
      setSetup(await api.startSetup())
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const openDisable = () => {
    setDialog('disable')
    setError(null)
  }

  const handleConfirm = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (code.length !== 6) return
    setBusy(true)
    setError(null)
    try {
      if (dialog === 'enable') {
        await api.enable(code)
        setEnabled(true)
        toast.success('Two-factor authentication is on')
      } else {
        await api.disable(code)
        setEnabled(false)
        toast.success('Two-factor authentication is off')
      }
      close()
    } catch (err) {
      setError(errorMessage(err))
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className={className}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-charcoal">Two-factor authentication</h2>
          <p className="text-sm text-charcoal/70 mt-1">
            {enabled
              ? 'On. You sign in with your password and a code from your authenticator app.'
              : 'Add a code from an authenticator app (such as Google Authenticator) to your sign-in.'}
          </p>
          {loadError && <p className="text-sm text-red-600 mt-1">{loadError}</p>}
        </div>
        {enabled === null && !loadError ? (
          <Spinner size="sm" />
        ) : (
          enabled !== null && (
            <Button
              variant="secondary"
              size="sm"
              onClick={enabled ? openDisable : openEnable}
            >
              {enabled ? 'Turn off' : 'Turn on'}
            </Button>
          )
        )}
      </div>

      <Modal
        open={dialog !== null}
        onClose={busy ? () => {} : close}
        title={
          dialog === 'enable'
            ? 'Turn on two-factor authentication'
            : 'Turn off two-factor authentication'
        }
        size="md"
      >
        <form onSubmit={handleConfirm} noValidate className="space-y-5">
          {dialog === 'enable' && (
            <>
              {!setup && busy && (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              )}
              {setup && (
                <>
                  <ol className="list-decimal pl-5 space-y-1 text-sm text-charcoal/80">
                    <li>Open your authenticator app and add a new account.</li>
                    <li>Scan this QR code, or type the key below.</li>
                    <li>Enter the 6-digit code the app shows.</li>
                  </ol>
                  <div className="flex justify-center">
                    <div className="rounded-lg bg-white p-3 border border-charcoal/10">
                      <QRCodeSVG value={setup.otpauth_uri} size={176} />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-charcoal/60">Can't scan? Enter this key:</p>
                    <p className="font-mono text-sm text-charcoal break-all mt-1 select-all">
                      {formatSecret(setup.secret)}
                    </p>
                  </div>
                </>
              )}
            </>
          )}

          {dialog === 'disable' && (
            <p className="text-sm text-charcoal/80">
              Enter the current code from your authenticator app to turn off two-factor
              authentication. Devices you asked us to remember will be forgotten.
            </p>
          )}

          {error && <Alert variant="error">{error}</Alert>}

          {(dialog === 'disable' || setup) && (
            <>
              <FormField label="Authentication code" required htmlFor="two-factor-settings-code">
                <Input
                  id="two-factor-settings-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  maxLength={7}
                  value={code}
                  onChange={(e) => setCode(normalizeTwoFactorCode(e.target.value))}
                  disabled={busy}
                  className="tracking-[0.4em] text-center text-lg"
                />
              </FormField>
              <p className="text-xs text-charcoal/60 flex items-start gap-1.5">
                <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
                Codes only work if your phone's clock is set automatically.
              </p>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="ghost" onClick={close} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant={dialog === 'disable' ? 'destructive' : 'primary'}
                  loading={busy}
                  disabled={busy || code.length !== 6}
                >
                  {dialog === 'enable' ? 'Turn on' : 'Turn off'}
                </Button>
              </div>
            </>
          )}
        </form>
      </Modal>
    </Card>
  )
}

export default TwoFactorSettingsCard
